import { AccessLevel, AccessObjectType, allAccessLevels } from '../access/types';
import { EntitlementKind, EntitlementScopeType } from './types';

export interface EntitlementKeyDefinition {
  kind: EntitlementKind;
  /** The scope types a row may carry; `null` is the organisation-wide row. */
  scopeTypes: ReadonlyArray<EntitlementScopeType | null>;
  /** The levels the key takes. Empty: it takes none and `level` must be null; otherwise required. */
  levels: readonly AccessLevel[];
  /**
   * Set when the level lands on an `access` row: the access object type each scope type projects
   * to, so the `fn_validate_access` rules for that type are checked before the projector runs.
   */
  accessObjectTypes?: Partial<Record<EntitlementScopeType, AccessObjectType>>;
}

/**
 * The keys with a fixed spelling. Parameterised keys (`coverage:list:<id>`) are matched by pattern
 * in `findEntitlementKeyDefinition`. A key the derivation would write without an entry here is
 * reported as an error, never written.
 */
export enum EntitlementKey {
  API = 'api',
  EU_PAI = 'eu_pai',
  FINANCIAL_MATERIALITY = 'financial_materiality',
  AI_CHAT = 'ai_chat',
  AI_CONNECTOR = 'ai_connector',
  // Product groups and the matrix: the lowercased `FeatureId` wherever the group has one.
  IMPACT_EXPORT = 'impact_export',
  EDIT_ALL_WEIGHT_SETS = 'edit_all_weight_sets',
  GISE_STATEMENTS = 'gise_statements',
  CSRD_GROUPFOLIO_CONSTITUENT_EXPORT = 'csrd_groupfolio_constituent_export',
  SDG_ALIGNMENT = 'sdg_alignment',
  EU_TAXONOMY = 'eu_taxonomy',
  NET_IMPACT = 'net_impact',
  SEARCHBAR = 'searchbar',
  PRODUCT_VIEW = 'product_view',
  PRODUCT_SEARCH = 'product_search',
  BLANKET_LIMITED = 'blanket_limited',
  DOWNLOAD_AS_EXCEL = 'download_as_excel',
  EDIT_CURRENT_WEIGHT_SET = 'edit_current_weight_set',
  TIME_TRACKING = 'time_tracking',
  DOWNLOAD_IMAGE = 'download_image',
  DMA_SCIENTIFIC_EVIDENCE = 'dma_scientific_evidence',
  // `SubscriptionFeature`s of the DMA plan, organisation-wide or for one company.
  DMA_RESULT_TAILORING = 'dma:result_tailoring',
  DMA_OPERATIONAL_DATA_INTEGRATION = 'dma:operational_data_integration',
  DMA_OPERATIONAL_DATA_INSIGHTS = 'dma:operational_data_insights',
  DMA_PRODUCT_ATTRIBUTES = 'dma:product_attributes',
  DMA_FINANCIAL_STATEMENTS_INPUT = 'dma:financial_statements_input',
  // The DMA plan above Starter, organisation-wide or for one client company of a consultant. Starter
  // is the absence of a plan item; a plan implies the features and the chat and connector caps of its tier.
  DMA_PLAN_PRO = 'dma:plan:pro',
  DMA_PLAN_ENTERPRISE = 'dma:plan:enterprise',
  // Recorded only: nothing reads these yet, so Ops can note what was sold. Starter is the absence of
  // a plan item for every check, so an item of it changes no tier or cap.
  DMA_PLAN_STARTER = 'dma:plan:starter',
  DMA_DOUBLE_MATERIALITY = 'dma:double_materiality',
  DMA_FINANCIAL_EFFECTS = 'dma:financial_effects',
  DMA_CLIMATE_RISK = 'dma:climate_risk',
  DMA_BENCHMARKING = 'dma:benchmarking',
  TAILORING = 'tailoring',
  CLIMATE = 'climate',
  FE = 'fe',
  CLIMATE_FINANCIAL_EFFECTS = 'climate_financial_effects',
  COVERAGE_STANDARD = 'coverage:standard',
  COVERAGE_EXTENDED = 'coverage:extended',
  // The CSRD data sets sold as packages. These names are provisional: the sold names replace them
  // here, in one place, before anything is switched on.
  COVERAGE_CSRD_DATA_SET_EXTENDED = 'coverage:csrd_data_set:extended-28-5k',
  COVERAGE_CSRD_DATA_SET_BENCHMARK = 'coverage:csrd_data_set:benchmark-8-3k',
  COVERAGE_ENTITY = 'coverage:entity',
  COVERAGE_CSRD_RESULT = 'coverage:csrd_result',
  // A consultant's member assigned to one company's assessment by membership of its group.
  COVERAGE_ASSESSMENT = 'coverage:assessment',
  SEGMENT_CLIENTS = 'segment:clients',
  SEGMENT_CLIENTS_AND_FREE_TRIAL_USERS = 'segment:clients_and_free_trial_users',
  SEGMENT_FREE_TRIAL = 'segment:free_trial',
  SEGMENT_INVESTOR_STANDARD_CLIENTS = 'segment:investor_standard_clients',
  SEGMENT_INVESTOR_FREE_CLIENTS = 'segment:investor_free_clients',
  SEGMENT_COMPANY_PLATFORM_STANDARD_CLIENTS = 'segment:company_platform_standard_clients',
  SEGMENT_COMPANY_PLATFORM_FREE_CLIENTS = 'segment:company_platform_free_clients',
  SEGMENT_COMPANY_PLATFORM_SELF_SIGNUP_USERS = 'segment:company_platform_self_signup_users',
  MANAGE_USERS = 'manage_users',
  MANAGE_SUBSCRIPTIONS = 'manage_subscriptions',
  CREATE_COMPANIES = 'create_companies',
  ASSESS_SUPPLIERS = 'assess_suppliers',
  // A consultant's member made admin of one company's assessment (`company_admin` membership).
  ASSESSMENT_ADMIN = 'assessment_admin',
  // Limits: the `QuotaKey`s of upserve/modules/usage-limits, spelled the same (bar the recorded-only ones below).
  CHAT_TOKENS = 'chat_tokens',
  PAYMENT_INIT = 'payment_init',
  COMPANY_WEBSITE_LOOKUP = 'company_website_lookup',
  COMPANY_WEBSITE_CHANGE = 'company_website_change',
  CUSTOM_COMPANIES_CREATED = 'custom_companies_created',
  SUPPLIER_COMPANIES_CREATED = 'supplier_companies_created',
  MCP_PRODUCT_IMPACT_REASONING = 'mcp_product_impact_reasoning',
  MCP_CONNECTOR_TOOL_CALLS = 'mcp_connector_tool_calls',
  MCP_IDENTIFY_COMPANY_SITES = 'mcp_identify_company_sites',
  MCP_GEOCODE_LOCATION = 'mcp_geocode_location',
  MCP_CLIMATE_RISK_GRID = 'mcp_climate_risk_grid',
  CLIMATE_SITES_ASSESSED = 'climate_sites_assessed',
  MCP_COMPUTE_FINANCIAL_EFFECTS = 'mcp_compute_financial_effects',
  DMA_IMPACT_EVIDENCE_GENERATION = 'dma_impact_evidence_generation',
  // Recorded only: quotas nothing counts against yet (no usage-limits QuotaKey of the same name).
  LIBRARY_COMPANIES_IN_PORTFOLIOS = 'library_companies_in_portfolios',
  CUSTOM_COMPANIES_CLIMATE_RISK = 'custom_companies_climate_risk',
  CUSTOM_COMPANIES_FINANCIAL_EFFECTS = 'custom_companies_financial_effects',
  SUPPLIER_COMPANIES_CLIMATE_RISK = 'supplier_companies_climate_risk',
  SUPPLIER_IMPORT_WEBSITE_LOOKUPS = 'supplier_import_website_lookups',
  EXTERNAL_METRICS_PER_MINUTE = 'external_metrics_per_minute',
  EXTERNAL_MATCH_PER_MINUTE = 'external_match_per_minute',
  EXTERNAL_PORTFOLIO_CREATE_PER_MINUTE = 'external_portfolio_create_per_minute',
  EXTERNAL_PROFILE_PER_MINUTE = 'external_profile_per_minute',
  EXTERNAL_METRICS_ALL_COMPANIES_INFLIGHT = 'external_metrics_all_companies_inflight',
  EXTERNAL_METRICS_ALL_PORTFOLIOS_INFLIGHT = 'external_metrics_all_portfolios_inflight',
  EXTERNAL_PORTFOLIO_CONSTITUENTS_UPLOAD_INFLIGHT = 'external_portfolio_constituents_upload_inflight',
  EXTERNAL_CSRD_DMA_CONSTITUENTS_INFLIGHT = 'external_csrd_dma_constituents_inflight',
  // The public MCP connector's caps (`usage-limits/public-call-limits.ts`): keyed on an
  // anonymous ChatGPT user or the shared pool, never on an organisation, so no
  // organisation ever holds an override row for them; listed so the registry names
  // every quota the usage-limits module defines.
  MCP_PUBLIC_CLIENT_CALLS_PER_DAY = 'mcp_public_client_calls_per_day',
  MCP_PUBLIC_CLIENT_CALLS_PER_MONTH = 'mcp_public_client_calls_per_month',
  MCP_PUBLIC_POOL_CALLS_PER_MINUTE = 'mcp_public_pool_calls_per_minute',
  MCP_PUBLIC_POOL_CALLS_PER_DAY = 'mcp_public_pool_calls_per_day',
  MCP_PUBLIC_CLIENT_REASONING_CALLS_PER_DAY = 'mcp_public_client_reasoning_calls_per_day',
  MCP_PUBLIC_CLIENT_REASONING_GENERATIONS_PER_DAY = 'mcp_public_client_reasoning_generations_per_day',
  MCP_PUBLIC_POOL_REASONING_GENERATIONS_PER_DAY = 'mcp_public_pool_reasoning_generations_per_day',
  // The Arvo API's rate limits (keyed per API key, organization or client address).
  ARVO_API_KEY_REQUESTS_PER_MINUTE = 'arvo_api_key_requests_per_minute',
  ARVO_ORG_REQUESTS_PER_MINUTE = 'arvo_org_requests_per_minute',
  ARVO_ORG_RUNNING_DECISIONS = 'arvo_org_running_decisions',
  ARVO_DRAFT_CREATE = 'arvo_draft_create',
  EEVO_EMAIL_LINK_PER_ADDRESS = 'eevo_email_link_per_address',
  EEVO_EMAIL_LINK_TOTAL = 'eevo_email_link_total',
  EEVO_INVITATIONS_PER_USER = 'eevo_invitations_per_user',
  EEVO_INVITE_EMAILS_PER_USER = 'eevo_invite_emails_per_user',
  EEVO_PROMO_REDEEM_PER_USER = 'eevo_promo_redeem_per_user',
  ROLLOUT_PROJECTION_PRIVILEGE = 'rollout:projection:privilege',
  // Rollout flags held today as resourceless privileges.
  ROLLOUT_FUND_UPLOAD = 'rollout:fund_upload',
  ROLLOUT_PRODUCT_SEGMENTS = 'rollout:product_segments',
  ROLLOUT_AI_CHAT_EU_RESIDENCY = 'rollout:ai_chat_eu_residency',
  ROLLOUT_CUSTOM_COMPANY_CLIMATE_RISK = 'rollout:custom_company_climate_risk',
  ROLLOUT_CLIMATE_ASSESSMENT_HORIZON = 'rollout:climate_assessment_horizon',
  ROLLOUT_UNIFIED_SHELL = 'rollout:unified_shell',
  ROLLOUT_CUSTOM_IMPACT_FRAMEWORKS = 'rollout:custom_impact_frameworks',
  ROLLOUT_CHAT_COMPANY_CREATION = 'rollout:chat_company_creation',
  ROLLOUT_CUSTOM_COMPANY_FINANCIAL_EFFECTS = 'rollout:custom_company_financial_effects',
}

/**
 * Standard and Extended coverage and the CSRD data sets, sold as one organisation-wide item each. The rights reader reads
 * them live, and their carrier groups' own access rows are what they cover, so they are never
 * projected.
 */
export const packageKeys: ReadonlySet<string> = new Set([
  EntitlementKey.COVERAGE_STANDARD,
  EntitlementKey.COVERAGE_EXTENDED,
  EntitlementKey.COVERAGE_CSRD_DATA_SET_EXTENDED,
  EntitlementKey.COVERAGE_CSRD_DATA_SET_BENCHMARK,
]);

// Coverage is access to what the organisation does not own. Ownership (`o`) is not an entitlement:
// owner rows stay with `entity_organization_relation` and the creation flows.
export const coverageLevels: readonly AccessLevel[] = allAccessLevels.filter(level => level !== AccessLevel.OWNER);

const organizationWideProduct: EntitlementKeyDefinition = {
  kind: EntitlementKind.PRODUCT,
  scopeTypes: [null],
  levels: [],
};

// A DMA plan or plan feature: organisation-wide from the subscription, for one company from a payment.
const dmaPlanFeature: EntitlementKeyDefinition = {
  kind: EntitlementKind.PRODUCT,
  scopeTypes: [null, EntitlementScopeType.COMPANY],
  levels: [],
};

// Keyed by the (company, scenario) pair. `r` reads the dataset; `e` also edits it (tailoring)
// or recalculates it (climate, FE).
const assessmentProduct: EntitlementKeyDefinition = {
  kind: EntitlementKind.PRODUCT,
  scopeTypes: [EntitlementScopeType.VARIANT_SCENARIO],
  levels: [AccessLevel.EDIT, AccessLevel.READ_ONLY],
};

// A reference to a coverage list. The list's items carry the levels, so the row takes none.
const coverageListReference: EntitlementKeyDefinition = {
  kind: EntitlementKind.COVERAGE,
  scopeTypes: [null],
  levels: [],
};

// Mirrored only: the projector never writes segment links.
const segment: EntitlementKeyDefinition = { kind: EntitlementKind.SEGMENT, scopeTypes: [null], levels: [] };

const organizationManagement: EntitlementKeyDefinition = {
  kind: EntitlementKind.MANAGEMENT,
  scopeTypes: [EntitlementScopeType.ORGANIZATION],
  levels: [],
};

// A quota override: organisation-wide, or for one client company of a consultant (`org::company`).
const limit: EntitlementKeyDefinition = {
  kind: EntitlementKind.LIMIT,
  scopeTypes: [null, EntitlementScopeType.COMPANY],
  levels: [],
};

const rollout: EntitlementKeyDefinition = { kind: EntitlementKind.ROLLOUT, scopeTypes: [null], levels: [] };

export const entitlementKeyDefinitions: Readonly<Record<EntitlementKey, EntitlementKeyDefinition>> = {
  [EntitlementKey.API]: organizationWideProduct,
  [EntitlementKey.EU_PAI]: organizationWideProduct,
  [EntitlementKey.FINANCIAL_MATERIALITY]: organizationWideProduct,
  [EntitlementKey.AI_CHAT]: organizationWideProduct,
  [EntitlementKey.AI_CONNECTOR]: organizationWideProduct,
  [EntitlementKey.IMPACT_EXPORT]: organizationWideProduct,
  [EntitlementKey.EDIT_ALL_WEIGHT_SETS]: organizationWideProduct,
  [EntitlementKey.GISE_STATEMENTS]: organizationWideProduct,
  [EntitlementKey.CSRD_GROUPFOLIO_CONSTITUENT_EXPORT]: organizationWideProduct,
  [EntitlementKey.SDG_ALIGNMENT]: organizationWideProduct,
  [EntitlementKey.EU_TAXONOMY]: organizationWideProduct,
  [EntitlementKey.NET_IMPACT]: organizationWideProduct,
  [EntitlementKey.SEARCHBAR]: organizationWideProduct,
  [EntitlementKey.PRODUCT_VIEW]: organizationWideProduct,
  [EntitlementKey.PRODUCT_SEARCH]: organizationWideProduct,
  [EntitlementKey.BLANKET_LIMITED]: organizationWideProduct,
  [EntitlementKey.DOWNLOAD_AS_EXCEL]: organizationWideProduct,
  [EntitlementKey.EDIT_CURRENT_WEIGHT_SET]: organizationWideProduct,
  [EntitlementKey.TIME_TRACKING]: organizationWideProduct,
  [EntitlementKey.DOWNLOAD_IMAGE]: organizationWideProduct,
  [EntitlementKey.DMA_SCIENTIFIC_EVIDENCE]: organizationWideProduct,
  [EntitlementKey.DMA_RESULT_TAILORING]: dmaPlanFeature,
  [EntitlementKey.DMA_OPERATIONAL_DATA_INTEGRATION]: dmaPlanFeature,
  [EntitlementKey.DMA_OPERATIONAL_DATA_INSIGHTS]: dmaPlanFeature,
  [EntitlementKey.DMA_PRODUCT_ATTRIBUTES]: dmaPlanFeature,
  [EntitlementKey.DMA_FINANCIAL_STATEMENTS_INPUT]: dmaPlanFeature,
  [EntitlementKey.DMA_PLAN_PRO]: dmaPlanFeature,
  [EntitlementKey.DMA_PLAN_ENTERPRISE]: dmaPlanFeature,
  [EntitlementKey.DMA_PLAN_STARTER]: dmaPlanFeature,
  [EntitlementKey.DMA_DOUBLE_MATERIALITY]: dmaPlanFeature,
  [EntitlementKey.DMA_FINANCIAL_EFFECTS]: dmaPlanFeature,
  [EntitlementKey.DMA_CLIMATE_RISK]: dmaPlanFeature,
  [EntitlementKey.DMA_BENCHMARKING]: dmaPlanFeature,
  [EntitlementKey.TAILORING]: assessmentProduct,
  [EntitlementKey.CLIMATE]: assessmentProduct,
  [EntitlementKey.FE]: assessmentProduct,
  // Read only: the euro figures within the climate lens have no edit or recalculation of their own.
  [EntitlementKey.CLIMATE_FINANCIAL_EFFECTS]: { ...assessmentProduct, levels: [AccessLevel.READ_ONLY] },
  [EntitlementKey.COVERAGE_STANDARD]: coverageListReference,
  [EntitlementKey.COVERAGE_EXTENDED]: coverageListReference,
  [EntitlementKey.COVERAGE_CSRD_DATA_SET_EXTENDED]: coverageListReference,
  [EntitlementKey.COVERAGE_CSRD_DATA_SET_BENCHMARK]: coverageListReference,
  // A grant on one entity at its legacy level: an `access` row on that entity.
  [EntitlementKey.COVERAGE_ENTITY]: {
    kind: EntitlementKind.COVERAGE,
    scopeTypes: [
      EntitlementScopeType.COMPANY,
      EntitlementScopeType.COMPANY_GROUP,
      EntitlementScopeType.PORTFOLIO,
      EntitlementScopeType.SOURCE,
      EntitlementScopeType.VARIANT_SCENARIO,
    ],
    levels: coverageLevels,
    accessObjectTypes: {
      [EntitlementScopeType.COMPANY]: AccessObjectType.COMPANY,
      [EntitlementScopeType.COMPANY_GROUP]: AccessObjectType.COMPANY_GROUP,
      [EntitlementScopeType.PORTFOLIO]: AccessObjectType.PORTFOLIO,
      [EntitlementScopeType.SOURCE]: AccessObjectType.SOURCE,
      [EntitlementScopeType.VARIANT_SCENARIO]: AccessObjectType.VARIANT_SCENARIO,
    },
  },
  // A read of one entity's CSRD results: an `access` row of object type `csrd` on that entity.
  [EntitlementKey.COVERAGE_CSRD_RESULT]: {
    kind: EntitlementKind.COVERAGE,
    scopeTypes: [EntitlementScopeType.COMPANY, EntitlementScopeType.COMPANY_GROUP, EntitlementScopeType.PORTFOLIO],
    levels: [AccessLevel.READ_ONLY],
    accessObjectTypes: {
      [EntitlementScopeType.COMPANY]: AccessObjectType.CSRD_RESULT_ENTITY,
      [EntitlementScopeType.COMPANY_GROUP]: AccessObjectType.CSRD_RESULT_ENTITY,
      [EntitlementScopeType.PORTFOLIO]: AccessObjectType.CSRD_RESULT_ENTITY,
    },
  },
  // Membership, not a grant row: it takes no level and projects to nothing.
  [EntitlementKey.COVERAGE_ASSESSMENT]: {
    kind: EntitlementKind.COVERAGE,
    scopeTypes: [EntitlementScopeType.COMPANY],
    levels: [],
  },
  [EntitlementKey.SEGMENT_CLIENTS]: segment,
  [EntitlementKey.SEGMENT_CLIENTS_AND_FREE_TRIAL_USERS]: segment,
  [EntitlementKey.SEGMENT_FREE_TRIAL]: segment,
  [EntitlementKey.SEGMENT_INVESTOR_STANDARD_CLIENTS]: segment,
  [EntitlementKey.SEGMENT_INVESTOR_FREE_CLIENTS]: segment,
  [EntitlementKey.SEGMENT_COMPANY_PLATFORM_STANDARD_CLIENTS]: segment,
  [EntitlementKey.SEGMENT_COMPANY_PLATFORM_FREE_CLIENTS]: segment,
  [EntitlementKey.SEGMENT_COMPANY_PLATFORM_SELF_SIGNUP_USERS]: segment,
  [EntitlementKey.MANAGE_USERS]: {
    kind: EntitlementKind.MANAGEMENT,
    scopeTypes: [EntitlementScopeType.USER_GROUP],
    levels: [],
  },
  [EntitlementKey.MANAGE_SUBSCRIPTIONS]: organizationManagement,
  [EntitlementKey.CREATE_COMPANIES]: organizationManagement,
  [EntitlementKey.ASSESS_SUPPLIERS]: organizationManagement,
  [EntitlementKey.ASSESSMENT_ADMIN]: {
    kind: EntitlementKind.MANAGEMENT,
    scopeTypes: [EntitlementScopeType.COMPANY],
    levels: [],
  },
  [EntitlementKey.CHAT_TOKENS]: limit,
  [EntitlementKey.PAYMENT_INIT]: limit,
  [EntitlementKey.COMPANY_WEBSITE_LOOKUP]: limit,
  [EntitlementKey.COMPANY_WEBSITE_CHANGE]: limit,
  [EntitlementKey.CUSTOM_COMPANIES_CREATED]: limit,
  [EntitlementKey.SUPPLIER_COMPANIES_CREATED]: limit,
  [EntitlementKey.MCP_PRODUCT_IMPACT_REASONING]: limit,
  [EntitlementKey.MCP_CONNECTOR_TOOL_CALLS]: limit,
  [EntitlementKey.MCP_IDENTIFY_COMPANY_SITES]: limit,
  [EntitlementKey.MCP_GEOCODE_LOCATION]: limit,
  [EntitlementKey.MCP_CLIMATE_RISK_GRID]: limit,
  [EntitlementKey.CLIMATE_SITES_ASSESSED]: limit,
  [EntitlementKey.MCP_COMPUTE_FINANCIAL_EFFECTS]: limit,
  [EntitlementKey.DMA_IMPACT_EVIDENCE_GENERATION]: limit,
  [EntitlementKey.LIBRARY_COMPANIES_IN_PORTFOLIOS]: limit,
  [EntitlementKey.CUSTOM_COMPANIES_CLIMATE_RISK]: limit,
  [EntitlementKey.CUSTOM_COMPANIES_FINANCIAL_EFFECTS]: limit,
  [EntitlementKey.SUPPLIER_COMPANIES_CLIMATE_RISK]: limit,
  [EntitlementKey.SUPPLIER_IMPORT_WEBSITE_LOOKUPS]: limit,
  [EntitlementKey.EXTERNAL_METRICS_PER_MINUTE]: limit,
  [EntitlementKey.EXTERNAL_MATCH_PER_MINUTE]: limit,
  [EntitlementKey.EXTERNAL_PORTFOLIO_CREATE_PER_MINUTE]: limit,
  [EntitlementKey.EXTERNAL_PROFILE_PER_MINUTE]: limit,
  [EntitlementKey.EXTERNAL_METRICS_ALL_COMPANIES_INFLIGHT]: limit,
  [EntitlementKey.EXTERNAL_METRICS_ALL_PORTFOLIOS_INFLIGHT]: limit,
  [EntitlementKey.EXTERNAL_PORTFOLIO_CONSTITUENTS_UPLOAD_INFLIGHT]: limit,
  [EntitlementKey.EXTERNAL_CSRD_DMA_CONSTITUENTS_INFLIGHT]: limit,
  [EntitlementKey.MCP_PUBLIC_CLIENT_CALLS_PER_DAY]: limit,
  [EntitlementKey.MCP_PUBLIC_CLIENT_CALLS_PER_MONTH]: limit,
  [EntitlementKey.MCP_PUBLIC_POOL_CALLS_PER_MINUTE]: limit,
  [EntitlementKey.MCP_PUBLIC_POOL_CALLS_PER_DAY]: limit,
  [EntitlementKey.MCP_PUBLIC_CLIENT_REASONING_CALLS_PER_DAY]: limit,
  [EntitlementKey.MCP_PUBLIC_CLIENT_REASONING_GENERATIONS_PER_DAY]: limit,
  [EntitlementKey.MCP_PUBLIC_POOL_REASONING_GENERATIONS_PER_DAY]: limit,
  [EntitlementKey.ARVO_API_KEY_REQUESTS_PER_MINUTE]: limit,
  [EntitlementKey.ARVO_ORG_REQUESTS_PER_MINUTE]: limit,
  [EntitlementKey.ARVO_ORG_RUNNING_DECISIONS]: limit,
  [EntitlementKey.ARVO_DRAFT_CREATE]: limit,
  [EntitlementKey.EEVO_EMAIL_LINK_PER_ADDRESS]: limit,
  [EntitlementKey.EEVO_EMAIL_LINK_TOTAL]: limit,
  [EntitlementKey.EEVO_INVITATIONS_PER_USER]: limit,
  [EntitlementKey.EEVO_INVITE_EMAILS_PER_USER]: limit,
  [EntitlementKey.EEVO_PROMO_REDEEM_PER_USER]: limit,
  [EntitlementKey.ROLLOUT_PROJECTION_PRIVILEGE]: rollout,
  [EntitlementKey.ROLLOUT_FUND_UPLOAD]: rollout,
  [EntitlementKey.ROLLOUT_PRODUCT_SEGMENTS]: rollout,
  [EntitlementKey.ROLLOUT_AI_CHAT_EU_RESIDENCY]: rollout,
  [EntitlementKey.ROLLOUT_CUSTOM_COMPANY_CLIMATE_RISK]: rollout,
  [EntitlementKey.ROLLOUT_CLIMATE_ASSESSMENT_HORIZON]: rollout,
  [EntitlementKey.ROLLOUT_UNIFIED_SHELL]: rollout,
  [EntitlementKey.ROLLOUT_CUSTOM_IMPACT_FRAMEWORKS]: rollout,
  [EntitlementKey.ROLLOUT_CHAT_COMPANY_CREATION]: rollout,
  [EntitlementKey.ROLLOUT_CUSTOM_COMPANY_FINANCIAL_EFFECTS]: rollout,
};

const coverageListKeyPrefix = 'coverage:list:';
// Lowercase, as Postgres prints a uuid: the key is compared as text, so one list has one spelling.
const coverageListKeyPattern = /^coverage:list:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** The key of a row referencing the coverage list with this id. */
export function toCoverageListEntitlementKey(listId: string): string {
  return `${coverageListKeyPrefix}${listId}`;
}

/** The coverage list id a `coverage:list:<id>` key references, or undefined for any other key. */
export function toCoverageListId(key: string): string | undefined {
  return coverageListKeyPattern.test(key) ? key.slice(coverageListKeyPrefix.length) : undefined;
}

export function isEntitlementKey(key: string): key is EntitlementKey {
  return (Object.values(EntitlementKey) as string[]).includes(key);
}

/** The registry entry for a key, or undefined when the key is not registered. */
export function findEntitlementKeyDefinition(key: string): EntitlementKeyDefinition | undefined {
  if (isEntitlementKey(key)) {
    return entitlementKeyDefinitions[key];
  }
  return toCoverageListId(key) === undefined ? undefined : coverageListReference;
}
